import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Group,
  Button,
  PageHeader,
  Paper,
  FormField,
  TextInput,
  Textarea,
  NumberInput,
  Switch,
  Alert,
  Loader,
  Text,
  AsyncSearchSelect,
  ImagePicker,
  type AsyncSearchPageResult,
} from '@vipso/shared';
import {
  useGetProductQuery,
  useAddUpdateProductMutation,
  useLazyListCategoriesQuery,
  useLazyListUnitsQuery,
  useAddUpdateCategoryMutation,
  useAddUpdateUnitMutation,
} from '../api/tenantApi';
import { uploadFile } from '../api/uploadFile';
import type { RootState } from '../store';

type FormState = {
  name: string;
  unit: string;
  categoryId: string;
  categoryName: string;
  description: string;
  quantity: number;
  pricePerQuantity: number;
  showSubse: boolean;
  addOnItem: boolean;
  addOnItemName: string;
  imageFileId: string;
  imageUrl: string;
};

const emptyForm = (): FormState => ({
  name: '',
  unit: '',
  categoryId: '',
  categoryName: '',
  description: '',
  quantity: 0,
  pricePerQuantity: 0,
  showSubse: true,
  addOnItem: false,
  addOnItemName: '',
  imageFileId: '',
  imageUrl: '',
});

export function ProductFormPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);

  const { data, isLoading, isError } = useGetProductQuery(id!, { skip: !isEdit });
  const [save, { isLoading: saving }] = useAddUpdateProductMutation();
  const [fetchCategories] = useLazyListCategoriesQuery();
  const [fetchUnits] = useLazyListUnitsQuery();
  const [addCategory] = useAddUpdateCategoryMutation();
  const [addUnit] = useAddUpdateUnitMutation();

  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');
  const [hydrated, setHydrated] = useState(!isEdit);

  useEffect(() => {
    if (!isEdit || hydrated || !data?.getProduct) return;
    const p = data.getProduct;
    setForm({
      name: p.name ?? '',
      unit: p.unit ?? '',
      categoryId: p.categoryId ? String(p.categoryId) : p.category?.id ? String(p.category.id) : '',
      categoryName: p.category?.name ?? '',
      description: p.description ?? '',
      quantity: Number(p.quantity ?? 0),
      pricePerQuantity: Number(p.pricePerQuantity ?? 0),
      showSubse: Boolean(p.showSubse),
      addOnItem: Boolean(p.addOnItem),
      addOnItemName: p.addOnItemName ?? '',
      imageFileId: p.imageFileId ? String(p.imageFileId) : '',
      imageUrl: p.imageUrl ?? '',
    });
    setHydrated(true);
  }, [isEdit, hydrated, data?.getProduct]);

  function patch(partial: Partial<FormState>) {
    setForm((prev) => ({ ...prev, ...partial }));
  }

  const loadUnits = useCallback(
    async (args: { search: string; page: number; pageLimit: number }): Promise<AsyncSearchPageResult> => {
      const res = await fetchUnits({
        search: args.search || undefined,
        page: args.page,
        pageLimit: args.pageLimit,
      }).unwrap();
      const rows = res.listUnits?.rows ?? [];
      return {
        rows: rows.map((u) => ({ value: u.name, label: u.name })),
        total: res.listUnits?.total ?? 0,
      };
    },
    [fetchUnits],
  );

  const loadCategories = useCallback(
    async (args: { search: string; page: number; pageLimit: number }): Promise<AsyncSearchPageResult> => {
      const res = await fetchCategories({
        search: args.search || undefined,
        page: args.page,
        pageLimit: args.pageLimit,
      }).unwrap();
      const rows = res.listCategories?.rows ?? [];
      return {
        rows: rows.map((c) => ({ value: c.id, label: c.name })),
        total: res.listCategories?.total ?? 0,
      };
    },
    [fetchCategories],
  );

  async function createUnitFromSearch(name: string) {
    const trimmed = name.trim();
    if (!trimmed) return;
    const created = await addUnit({ name: trimmed }).unwrap();
    patch({ unit: created.addUpdateUnit.name });
  }

  async function createCategoryFromSearch(name: string) {
    const trimmed = name.trim();
    if (!trimmed) return;
    const created = await addCategory({ name: trimmed }).unwrap();
    patch({
      categoryId: created.addUpdateCategory.id,
      categoryName: created.addUpdateCategory.name,
    });
  }

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = t('products.validation.name');
    if (!form.unit.trim()) next.unit = t('products.validation.unit');
    if (!form.categoryId) next.categoryId = t('products.validation.category');
    if (form.addOnItem && !form.addOnItemName.trim()) {
      next.addOnItemName = t('products.validation.addonName');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit() {
    setSubmitError('');
    if (!validate()) return;

    const payload: Record<string, unknown> = {
      name: form.name.trim(),
      unit: form.unit.trim(),
      categoryId: Number(form.categoryId),
      description: form.description.trim() || null,
      quantity: Number(form.quantity) || 0,
      pricePerQuantity: Number(form.pricePerQuantity) || 0,
      showSubse: form.showSubse,
      addOnItem: form.addOnItem,
      addOnItemName: form.addOnItem ? form.addOnItemName.trim() : null,
      imageFileId: form.imageFileId ? Number(form.imageFileId) : null,
    };
    if (isEdit && id) payload.id = id;

    try {
      const result = await save(payload).unwrap();
      const savedId = result.addUpdateProduct.id;
      navigate(`/products/${savedId}`, { replace: true });
    } catch (e: unknown) {
      const msg =
        (e as { data?: { message?: string }; message?: string })?.data?.message ??
        (e as { message?: string })?.message ??
        t('products.saveFailed');
      setSubmitError(msg);
    }
  }

  if (isEdit && (isLoading || !hydrated) && !isError) {
    return (
      <Stack align="center" py="xl">
        <Loader />
      </Stack>
    );
  }

  if (isEdit && (isError || !data?.getProduct)) {
    return (
      <Alert color="red" title={t('products.notFound')}>
        <Button variant="light" mt="sm" onClick={() => navigate('/products')}>
          {t('products.backToList')}
        </Button>
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <PageHeader
        title={isEdit ? t('products.edit') : t('products.add')}
        description={isEdit ? t('products.editDescription') : t('products.addDescription')}
        actions={
          <Button variant="default" onClick={() => navigate(isEdit && id ? `/products/${id}` : '/products')}>
            {t('common.cancel')}
          </Button>
        }
      />

      {submitError ? (
        <Alert color="red" title={t('common.saveFailed')}>
          {submitError}
        </Alert>
      ) : null}

      <Paper p="md" withBorder radius="md">
        <Stack gap="md">
          <ImagePicker
            value={form.imageUrl || null}
            label={t('products.image')}
            shape="square"
            size={88}
            onUpload={async (file) => {
              if (!accessToken) throw new Error(t('auth.notSignedIn'));
              const uploaded = await uploadFile(file, accessToken);
              patch({ imageFileId: uploaded.id, imageUrl: uploaded.url });
            }}
            onClear={() => patch({ imageFileId: '', imageUrl: '' })}
          />

          <FormField label={t('products.namePlaceholder')} required error={errors.name}>
            <TextInput
              value={form.name}
              placeholder={t('products.namePlaceholder')}
              onChange={(e) => patch({ name: e.currentTarget.value })}
            />
          </FormField>

          <FormField label={t('products.unit')} required error={errors.unit}>
            <AsyncSearchSelect
              placeholder={t('products.unitSearch')}
              value={form.unit}
              valueLabel={form.unit}
              onChange={(value, label) => patch({ unit: label || value })}
              fetchPage={loadUnits}
            />
            <Group gap="xs" mt={6}>
              <Text size="xs" c="dimmed">
                {t('products.addUnitHint')}
              </Text>
              <Button
                size="xs"
                variant="light"
                onClick={() => {
                  const name = window.prompt(t('products.newUnitPrompt'));
                  if (name) void createUnitFromSearch(name);
                }}
              >
                {t('products.addUnit')}
              </Button>
            </Group>
          </FormField>

          <FormField label={t('products.category')} required error={errors.categoryId}>
            <AsyncSearchSelect
              placeholder={t('products.categorySearch')}
              value={form.categoryId}
              valueLabel={form.categoryName}
              onChange={(value, label) => patch({ categoryId: value, categoryName: label })}
              fetchPage={loadCategories}
            />
            <Group gap="xs" mt={6}>
              <Text size="xs" c="dimmed">
                {t('products.missingCategory')}
              </Text>
              <Button
                size="xs"
                variant="light"
                onClick={() => {
                  const name = window.prompt(t('products.newCategoryPrompt'));
                  if (name) void createCategoryFromSearch(name);
                }}
              >
                {t('products.addCategory')}
              </Button>
            </Group>
          </FormField>

          <FormField label={t('products.productDescription')}>
            <Textarea
              minRows={3}
              value={form.description}
              placeholder={t('products.descriptionPlaceholder')}
              onChange={(e) => patch({ description: e.currentTarget.value })}
            />
          </FormField>

          <Group grow align="flex-start">
            <FormField label={t('products.quantity')}>
              <NumberInput
                min={0}
                decimalScale={3}
                value={form.quantity}
                onChange={(val) => patch({ quantity: Number(val) || 0 })}
              />
            </FormField>
            <FormField label={t('products.pricePerQuantityLabel')}>
              <NumberInput
                min={0}
                decimalScale={2}
                value={form.pricePerQuantity}
                onChange={(val) => patch({ pricePerQuantity: Number(val) || 0 })}
              />
            </FormField>
          </Group>

          <Group justify="space-between" align="center">
            <Text size="sm" fw={500}>
              {t('products.subsequentQuantity')}
            </Text>
            <Switch
              checked={form.showSubse}
              onChange={(e) => patch({ showSubse: e.currentTarget.checked })}
              label={form.showSubse ? t('common.on') : t('common.off')}
              labelPosition="left"
            />
          </Group>

          <Group justify="space-between" align="center">
            <Text size="sm" fw={500}>
              {t('products.addOnItem')}
            </Text>
            <Switch
              checked={form.addOnItem}
              onChange={(e) =>
                patch({
                  addOnItem: e.currentTarget.checked,
                  addOnItemName: e.currentTarget.checked ? form.addOnItemName : '',
                })
              }
              label={form.addOnItem ? t('common.on') : t('common.off')}
              labelPosition="left"
            />
          </Group>

          {form.addOnItem ? (
            <FormField label={t('products.addOnItemName')} required error={errors.addOnItemName}>
              <TextInput
                value={form.addOnItemName}
                placeholder={t('products.addOnPlaceholder')}
                onChange={(e) => patch({ addOnItemName: e.currentTarget.value })}
              />
            </FormField>
          ) : null}

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => navigate(isEdit && id ? `/products/${id}` : '/products')}>
              {t('common.cancel')}
            </Button>
            <Button loading={saving} onClick={() => void handleSubmit()}>
              {isEdit ? t('products.update') : t('products.save')}
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
}
